import { useCallback, useMemo } from 'react';
import type { PreloadedEntryPoint, PreloadedQuery } from 'react-relay';
import useHydrationQueryLoaderQuery, {
	type useHydrationQueryLoaderQuery as HydrationRelayQueryType,
} from '@atlassian/jira-relay/src/__generated__/useHydrationQueryLoaderQuery.graphql';
import type { AnyEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import main_IssueNavigatorQuery from '@atlassian/jira-relay/src/__generated__/main_IssueNavigatorQuery.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import type { jqlBuilderWithAiEntryPoint as JqlBuilderWithAiEntryPointType } from '@atlassian/jira-jql-builder-with-ai/src/ui/entrypoint.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';

type ServerTimeCallback = () => {
	serverTime: number | undefined;
	hydrationServerTime: number | undefined;
	searchServerTime: number | undefined;
};

/*
 * Approximates the server time for the query by first:
 * - checking the SSR-origin response time (if it doesn't time out),
 * - and then the client-origin response time.
 */
const getServerTime = (queryName: string): number | undefined => {
	return (
		globalThis?.window?.SPA_STATE?.['ssr/relay_fetch']?.[queryName]?.duration ??
		globalThis?.window?.performance
			.getEntriesByType('measure')
			.find(({ name }) => name.includes(queryName))?.duration
	);
};

export const useServerTimeUFO = () => {
	const serverTimeCallback = useCallback<ServerTimeCallback>(() => {
		if (
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('add_servertime_to_ufo_for_nin') &&
			!__SERVER__ &&
			globalThis?.window?.performance &&
			globalThis?.window?.SPA_STATE
		) {
			const hydrationServerTime = getServerTime(useHydrationQueryLoaderQuery.params.name);
			const searchServerTime = getServerTime(main_IssueNavigatorQuery.params.name);

			return {
				// Only add statsig ingested custom serverTime data if both server times are available
				serverTime:
					hydrationServerTime && searchServerTime
						? hydrationServerTime + searchServerTime
						: undefined,
				hydrationServerTime,
				searchServerTime,
			};
		}
		return {
			serverTime: undefined,
			hydrationServerTime: undefined,
			searchServerTime: undefined,
		};
	}, []);

	return fg('add_servertime_to_ufo_for_nin') ? serverTimeCallback : undefined;
};

export type NestedEntryPoints = {
	nativeIssueTableEntryPoint?: PreloadedEntryPoint<AnyEntryPoint>;
	jqlBuilderWithAiEntryPoint?: PreloadedEntryPoint<typeof JqlBuilderWithAiEntryPointType>;
};

export const useJqlBuilderProps = (
	hydrationQueryReference: PreloadedQuery<HydrationRelayQueryType> | null,
	preloadedEntryPoints?: NestedEntryPoints,
	projectKey?: string,
) => {
	return useMemo(
		() => ({
			maybeHydrationQueryReference: hydrationQueryReference || undefined,
			preloadedEntryPoints: {
				jqlBuilderWithAiEntryPoint: preloadedEntryPoints?.jqlBuilderWithAiEntryPoint,
			},
			...(projectKey && expVal('list_lovability_improving_filters_exp', 'isEnabled', false)
				? { projectContext: projectKey }
				: {}),
		}),
		[hydrationQueryReference, preloadedEntryPoints?.jqlBuilderWithAiEntryPoint, projectKey],
	);
};
